import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { customerReviews } from '../data/reviewsData';

const ReviewCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  React.useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === customerReviews.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000); // 6 seconds per review

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? customerReviews.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === customerReviews.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentReview = customerReviews[currentIndex];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-cyan-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            See our reviews directly from our Checkatrade profile!
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Real feedback from our satisfied customers
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            {/* Review Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 min-h-[500px] transition-all duration-500">
              {/* Score Badge */}
              <div className="flex items-center justify-center mb-6">
                <div className="bg-gradient-to-br from-cyan-600 to-teal-600 text-white rounded-full px-8 py-4 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <Star className="h-8 w-8 fill-current" />
                    <span className="text-4xl font-bold">{currentReview.score}</span>
                    <span className="text-xl">/10</span>
                  </div>
                </div>
              </div>

              {/* Review Title */}
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">
                {currentReview.title}
              </h3>

              {/* Review Text */}
              <div className="mb-8">
                <p className="text-lg text-slate-700 leading-relaxed text-center italic whitespace-pre-line">
                  "{currentReview.review}"
                </p>
              </div>

              {/* Customer Info */}
              <div className="border-t border-slate-200 pt-6">
                <div className="flex flex-col md:flex-row items-center justify-between text-slate-600">
                  <div className="mb-2 md:mb-0">
                    <p className="font-semibold text-slate-900">{currentReview.customerName}</p>
                    <p className="text-sm">{currentReview.location}</p>
                  </div>
                  <div className="text-sm text-slate-500">
                    {currentReview.date}
                  </div>
                </div>
              </div>

              {/* Checkatrade Badge */}
              <div className="mt-6 text-center">
                <span className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-semibold">
                  ✓ Verified Checkatrade Review
                </span>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full p-4 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
              aria-label="Previous review"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full p-4 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
              aria-label="Next review"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Dot Navigation */}
          <div className="flex gap-2 mt-8 justify-center overflow-x-auto pb-2">
            {customerReviews.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`flex-shrink-0 transition-all duration-300 rounded-full ${
                  currentIndex === index
                    ? 'w-8 h-3 bg-cyan-600'
                    : 'w-3 h-3 bg-slate-300 hover:bg-cyan-400'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>

          {/* Info Text */}
          <div className="mt-8 text-center text-sm text-slate-600 bg-cyan-50 rounded-lg p-4">
            <p className="font-semibold mb-2">⭐ Verified Checkatrade Customer Reviews</p>
            <p>Real feedback from our satisfied customers</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewCarousel;
